import React, { useContext, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { RootStoreProvider, RootStoreContext } from './core/root-store';
import { JobRoleProvider } from './context/job-role.context';
import Home from './main/home';
import Login from './auth/login';
import SignUp from './auth/signup';
import Dashboard from './dashboard/dashboard.container';
import OnboardingStepContainer from './onboarding/onboarding-steps.container';
import PTestStepsContainer from './onboarding/p-test-steps.container';
import PersonalityResultsContainer from './onboarding/personality-results.container';
import RoleSelectionContainer from './role-selection/role-selection.container';
import { OnboardingStep } from './onboarding/onboarding.type';
import { updateUserProfileInDatabase } from './user/user.service';
import { auth } from './firebase/config';
import { getLastCompletedStep } from './helpers';
import Loader from './ui/loader.ui';
import { useSessionTimeout } from './auth/session-timeout.hook';
import { signOut } from 'firebase/auth';
import { UserRole } from './user/user.type';

const ProtectedRoute = observer(({ children }: { children: React.ReactNode }) => {
  const rootStore = useContext(RootStoreContext);
  const userStore = rootStore.userStore;
  const onboardingStore = rootStore.onboardingStore;
  const location = useLocation();
  const userRole = userStore.user?.role;

  // Handle onboardingStep assignment for mobile app users
  React.useEffect(() => {
    const assignOnboardingStep = async () => {
      const currentStep = userStore.user?.webOnboarding?.onboardingStep;

      if (userStore.user && (!currentStep || !onboardingStore.stepOrder.includes(currentStep))) {
        const lastCompletedStep = getLastCompletedStep(userStore.user, onboardingStore.stepOrder);

        if (lastCompletedStep) {
          try {
            let userId = userStore.user?.uid;
            if (!userId && auth.currentUser) {
              userId = auth.currentUser.uid;
            }
            if (userId) {
              const updatedWebOnboarding = userRole === UserRole.SoftwareEngineer
                ? {
                  appliedFor: userStore.user.webOnboarding?.appliedFor,
                  optedIn: userStore.user.webOnboarding?.optedIn,
                  meetingInviteSent: userStore.user.webOnboarding?.meetingInviteSent,
                  onboardingStep: lastCompletedStep,
                }
                : {
                  onboardingStep: lastCompletedStep,
                };

              await updateUserProfileInDatabase(userId, {
                webOnboarding: updatedWebOnboarding,
              });
              userStore.updateUserData({
                webOnboarding: updatedWebOnboarding,
              });
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to assign onboardingStep:', error);
          }
        }
      }
    };

    assignOnboardingStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStore, userStore.user?.uid, onboardingStore.stepOrder]);

  if (!userStore.loaded) {
    return <Loader />;
  }

  if (!userStore.isAuthenticated || !userStore.user) {
    const lastUserRole = sessionStorage.getItem('lastUserRole');
    const jobRoleId = sessionStorage.getItem('currentJobRoleId');

    let redirectTo = '/';
    if (lastUserRole === UserRole.Founder) {
      redirectTo = '/founder';
    } else if (jobRoleId) {
      redirectTo = `/${jobRoleId}`;
    }

    return <Navigate to={redirectTo} replace />;
  }

  const lastStep = onboardingStore.stepOrder[onboardingStore.stepOrder.length - 1];
  const lastCompletedStepFromFlags = getLastCompletedStep(userStore.user, onboardingStore.stepOrder);
  const isOnboardingComplete =
    userStore.user.webOnboarding?.onboardingStep === OnboardingStep.OnboardingCompleted ||
    userStore.user.hasCompletedPTest ||
    lastCompletedStepFromFlags === lastStep;
  const hasCompletedPTest = userStore.user.hasCompletedPTest;

  const isOnOnboardingPage = location.pathname === '/onboarding' || location.pathname.includes('/onboarding');
  const isOnPTestPage = location.pathname === '/personality-test';
  const isOnRoleSelectionPage = location.pathname === '/role-selection';

  // If user has no role, redirect to role selection page
  if (!userStore.user.role && !isOnRoleSelectionPage) {
    return <Navigate to="/role-selection" replace />;
  }

  // If onboarding is not complete, redirect to onboarding
  if (!isOnboardingComplete && !isOnOnboardingPage) {
    return <Navigate to="/onboarding" replace state={{ returned: userStore.isReturningUser }} />;
  }

  // If onboarding is complete but P-Test is not, redirect to P-Test
  if (isOnboardingComplete && !hasCompletedPTest && !isOnPTestPage) {
    return <Navigate to="/personality-test" replace state={{ returned: userStore.isReturningUser }} />;
  }

  // If everything is complete, redirect to dashboard (unless already there)
  if (isOnboardingComplete && hasCompletedPTest && location.pathname !== '/dashboard') {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
});

// Component to handle onboarding route with step initialization
const OnboardingRoute = observer(() => {
  const rootStore = useContext(RootStoreContext);
  const userStore = rootStore.userStore;
  const onboardingStore = rootStore.onboardingStore;
  const navigate = useNavigate();
  const location = useLocation();
  const [isInitializing, setIsInitializing] = React.useState(true);

  // Check if this is a returning user - only use userStore.isReturningUser flag
  const returned = userStore.isReturningUser;

  useEffect(() => {
    const initializeOnboarding = async () => {
      if (userStore.user) {
        // Initialize onboarding store with user data
        onboardingStore.initializeFromUser();

        // Determine the correct step the user should be on
        const lastCompletedStep = getLastCompletedStep(userStore.user, onboardingStore.stepOrder);

        // Start with phone number as first onboarding step
        const defaultStep = OnboardingStep.PhoneNumber;

        const correctNextStep = lastCompletedStep
          ? onboardingStore.getNextStep(lastCompletedStep)
          : defaultStep;

        // Handle step parameter from URL
        const searchParams = new URLSearchParams(location.search);
        const stepParam = searchParams.get('step');

        if (stepParam && Object.values(OnboardingStep).includes(stepParam as OnboardingStep)) {
          // If URL step is different from the correct step, redirect to correct step
          if (stepParam !== correctNextStep) {
            navigate(`/onboarding?step=${correctNextStep}`, { replace: true, state: { returned: userStore.isReturningUser } });
            return;
          }
          // URL step is correct, use it
          onboardingStore.updateOnboardingStep(stepParam as OnboardingStep);
        } else {
          // No step parameter or invalid step, redirect to correct step
          navigate(`/onboarding?step=${correctNextStep}`, { replace: true, state: { returned: userStore.isReturningUser } });
          return;
        }

        // Mark initialization complete
        setIsInitializing(false);
      }
    };

    initializeOnboarding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStore.user?.uid, navigate, location.search, userStore.isReturningUser]);

  if (!userStore.user) {
    return <Loader />;
  }

  if (isInitializing) {
    return null;
  }

  return <OnboardingStepContainer returned={returned} />;
});

// Component to handle P-Test route with returned state detection
const PTestRoute = observer(() => {
  const rootStore = useContext(RootStoreContext);
  const userStore = rootStore.userStore;

  // Check if this is a returning user - only use userStore.isReturningUser flag
  const returned = userStore.isReturningUser;

  return <PTestStepsContainer returned={returned} />;
});

// Component to handle role selection route with authentication check
const RoleSelectionRoute = observer(() => {
  const rootStore = useContext(RootStoreContext);
  const userStore = rootStore.userStore;

  if (!userStore.loaded) {
    return <Loader />;
  }

  if (!userStore.isAuthenticated || !userStore.user) {
    return <Navigate to="/" replace />;
  }

  return <RoleSelectionContainer />;
});

const AppContent = observer(() => {
  const rootStore = useContext(RootStoreContext);
  const userStore = rootStore.userStore;

  const handleTimeout = async () => {
    if (userStore.isAuthenticated) {
      try {
        await signOut(auth);
        userStore.clearUser();
      } catch (error) {
      }
    }
  };

  useSessionTimeout({
    onTimeout: handleTimeout,
    isAuthenticated: userStore.isAuthenticated,
  });

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route path="/" element={<JobRoleProvider><Home /></JobRoleProvider>} />
          <Route path="/founder" element={<JobRoleProvider><Home /></JobRoleProvider>} />
          <Route path="/:jobRoleId" element={<JobRoleProvider><Home /></JobRoleProvider>} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route
            path="/role-selection"
            element={<RoleSelectionRoute />}
          />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <JobRoleProvider>
                  <OnboardingRoute />
                </JobRoleProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/personality-test"
            element={
              <ProtectedRoute>
                <PTestRoute />
              </ProtectedRoute>
            }
          />
          <Route
            path="/personality-results"
            element={
              <ProtectedRoute>
                <PersonalityResultsContainer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <JobRoleProvider>
                  <Dashboard />
                </JobRoleProvider>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

      </div>
    </Router>
  );
});

const App = () => {
  return (
    <RootStoreProvider>
      <AppContent />
    </RootStoreProvider>
  );
};

export default App;
